import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { finalize, firstValueFrom } from 'rxjs';
import { toIsoCurrency } from '../../../../../shared/utils/xtm-currency.utils';
import { ContentTypeLabelPipe } from '../../../../../shared/pipes/content-type-label.pipe';
import {
  BreadcrumbComponent,
  BreadcrumbLevel,
} from '../../../../../shared/components/breadcrumb/breadcrumb.component';
import { SpinnerComponent } from '../../../../../shared/components/az-spinner/spinner.component';
import {
  isJobStatusCancellable,
  JOB_STATUSES_NEW_TAGS,
  LANGUAGES,
  QUALIFYING_STATUSES_FOR_REVISED_DTP,
  REJECTED_REQUEST_SUPPORT_EMAIL,
  REJECTED_REQUEST_SUPPORT_SUBJECT,
  TRANSLATED_FILES_ENABLED_STATUSES,
} from '../../../../../shared/config/constants';
import { S3FilesService } from '../../../../../shared/services/s3files.service';
import {
  S3DestinationFolder,
  TranslationFileType,
} from '../../../../../shared/models/s3-destination-folder';
import {
  Estimate,
  Quote,
  VendorOrder,
  VendorOrderFile,
} from '../../../../../shared/models/vendor-order.model';
import { VendorOrdersService } from '../services/vendor-orders.service';
import { VendorService } from '../../../../../shared/services/vendor.service';
import { AuthService } from '../../../../auth/services/auth.service';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { PopupService } from '../../../../../shared/components/popup/popup.service';
import { QuotesComponent } from '../quotes/quotes.component';
import { TranslatedFilesComponent } from '../translated-files/translated-files.component';
import { MessagesComponent } from '../../../../../features/messages/messages.component';
import {
  MESSAGES_LEGACY_ENABLED_STATUSES,
  MESSAGES_LEGACY_READONLY_STATUSES,
} from '../../../../../features/messages/messages.constants';
import {
  MessagePolicyEvaluation,
  computeMessagePolicy,
} from '../../../../../features/messages/message-retention-policy.util';
import { ToolbarTitleService } from '../../../components/toolbar/toolbar-title.service';
import {
  CancelFlowState,
  CancelReasonDialogResult,
  formatCancellationReason,
} from './cancel-job/cancel-job.types';
import { CANCELLATION_REASON_REANALYSIS_APPROVAL_EXPIRED } from '../../../../../shared/utils/cancellation-reason.utils';
import { CancelJobScopeDialogComponent } from './cancel-job/scope/cancel-job-scope-dialog.component';
import { CancelJobReasonDialogComponent } from './cancel-job/reason/cancel-job-reason-dialog.component';
import { CancelJobSuccessDialogComponent } from './cancel-job/success/cancel-job-success-dialog.component';
import { CancelJobSuccessContentComponent } from './cancel-job/quotes-success/cancel-job-success-content.component';
import { CancelJobConfirmContentComponent } from './cancel-job/quotes-confirm/cancel-job-confirm-content.component';
import { FileSelectionModalComponent } from './cancel-job/file-selection/file-selection-modal.component';
import { ConfirmationModalComponent } from './cancel-job/files-cancellation-summary/confirmation-modal.component';
import {
  FinalCostDialogResult,
  ReviewFinalCostModalComponent,
} from './review-final-cost/review-final-cost-modal.component';
import {
  ApproveFinalCostDialogResult,
  ApproveFinalCostModalComponent,
  ApproveFinalCostResult,
} from './approve-final-cost/approve-final-cost-modal.component';
import { ApproveFinalCostSuccessComponent } from './approve-final-cost/success/approve-final-cost-success.component';
import {
  RejectFinalCostDialogResult,
  RejectFinalCostModalComponent,
} from './reject-final-cost/reject-final-cost-modal.component';

/** Represents a source file with its translation type classification. */
interface SourceFile {
  name: string;
  size: string;
  bytes: number;
}

type FileSortColumn = 'name' | 'size';

interface FileSortState {
  column: FileSortColumn;
  descending: boolean;
}

type FileTableType = 'basic' | 'dtp' | 'reference';
type JobDetailTabId = 'details' | 'quotes' | 'translated-files' | 'messages';

interface JobDetailTab {
  id: JobDetailTabId;
  label: string;
}

interface FinalCostIncreaseBanner {
  percent: string;
  currency: string;
  amount: string;
  estimateName?: string;
}

interface FinalCostReviewContext {
  estimate: Estimate;
  acceptedQuote: Quote;
  reanalysis: Quote;
  approvedCost: number;
  reanalysisCost: number;
}

/** View model for the job detail page. */
interface JobDetailView {
  jobUid: string;
  jobNumber: string;
  jobName: string;
  requestDate: string;
  targetDate: string;
  statusLabel: string;
  statusKey: string;
  statusIcon: string | null;
  contentType: string;
  therapeuticArea: string;
  costCentre: string;
  targetAudience: string;
  translationCertificate: string;
  documentTone: string;
  additionalInfo: string;
  cancellationReason: string;
  cancellationDate?: string;
  sourceLanguage: string;
  targetLanguages: string[];
  basicFormatFiles: SourceFile[];
  dtpFiles: SourceFile[];
  referenceFiles: SourceFile[];
}

const LOCALE_LOOKUP = new Map<string, string>(Object.entries(LANGUAGES));

const STATUS_ICON_MAP: Readonly<Record<string, string>> = {
  'Quotes Pending': 'schedule',
  'Quotes to be Reviewed': 'request_quote',
  'In Progress': 'article_person',
  Cancelled: 'cancel',
  Completed: 'check_circle',
  Rejected: 'do_not_disturb_on',
  'Not Supported': 'warning',
};

@Component({
  selector: 'app-job-detail',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    MatIconModule,
    RouterLink,
    SpinnerComponent,
    ContentTypeLabelPipe,
    QuotesComponent,
    TranslatedFilesComponent,
    MessagesComponent,
    SpinnerComponent,
  ],
  templateUrl: './job-detail.component.html',
  styleUrl: './job-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly vendorOrdersService = inject(VendorOrdersService);
  private readonly vendorService = inject(VendorService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly s3FilesService = inject(S3FilesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toolbarTitleService = inject(ToolbarTitleService);
  private readonly matDialog = inject(MatDialog);
  private readonly popupService = inject(PopupService);
  readonly notificationService = inject(NotificationService);
  private readonly order = signal<VendorOrder | null>(null);
  private readonly hasWindow = typeof window !== 'undefined';
  private readonly selectedFilters = signal<string[]>([]);
  private readonly notificationSnapshotOnEntry = signal('');
  private readonly sourceFiles = signal<SourceFile[]>([]);
  private readonly referenceFiles = signal<SourceFile[]>([]);
  readonly activeTab = signal<JobDetailTabId>('details');
  readonly renderedTab = signal<JobDetailTabId>('details');
  readonly tabContentState = signal<'idle' | 'exiting' | 'entering'>('idle');
  readonly tabIndicatorWidth = signal(0);
  readonly tabIndicatorOffset = signal(0);
  readonly tabIndicatorTransform = computed(
    () => `translateX(${this.tabIndicatorOffset()}px)`,
  );
  private readonly vendorName: string | undefined = (
    history.state as { vendorName?: string } | null
  )?.vendorName;
  private readonly rawJobStatus: string | undefined = (
    history.state as { jobStatus?: string } | null
  )?.jobStatus;
  readonly hasNewMessage = signal<boolean>(
    (history.state as { newMessage?: boolean } | null)?.newMessage === true,
  );
  readonly basicFilesSort = signal<FileSortState>({
    column: 'name',
    descending: false,
  });
  readonly dtpFilesSort = signal<FileSortState>({
    column: 'name',
    descending: false,
  });
  readonly referenceFilesSort = signal<FileSortState>({
    column: 'name',
    descending: false,
  });
  readonly isLoading = signal(true);
  readonly isFilesLoading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly cancelFlowState = signal<CancelFlowState>({
    scope: null,
    reason: '',
    isSubmitting: false,
  });
  private static readonly TAB_FADE_DURATION_MS = 180;

  private fadeExitTimerId: ReturnType<typeof setTimeout> | null = null;
  private fadeEnterTimerId: ReturnType<typeof setTimeout> | null = null;
  private reloadTimerId: ReturnType<typeof setTimeout> | null = null;

  readonly tabs = computed<readonly JobDetailTab[]>(() => {
    const baseTabs: JobDetailTab[] = [
      { id: 'details', label: 'Job Details' },
      { id: 'quotes', label: 'Quotes' },
    ];

    if (this.isInProgressOrLater()) {
      baseTabs.push({ id: 'messages', label: 'Messages' });
    }

    if (this.isTranslatedFilesTabEnabled()) {
      baseTabs.push({ id: 'translated-files', label: 'Translated Files' });
    }

    return baseTabs;
  });

  readonly isTranslatedFilesTabEnabled = computed(() => {
    const order = this.order();
    const status = order?.jobStatus;
    if (!status) return false;
    const normalized = status.toUpperCase().replaceAll(/[\s-]/g, '_');

    // Hide when all quotes expired — no files were ever translated
    if (order?.allQuotesExpired === true) return false;

    // For CANCELLED status, only show if purchaseOrderId exists
    if (normalized === 'CANCELLED') {
      const po = order?.purchaseOrderId;
      return typeof po === 'string' ? po.trim().length > 0 : Boolean(po);
    }

    // For other statuses, check against enabled statuses list
    // (REJECTED and other statuses not in the list will return false)
    return (TRANSLATED_FILES_ENABLED_STATUSES as readonly string[]).includes(
      normalized,
    );
  });

  readonly isInProgressJob = computed(() => {
    const status = this.order()?.jobStatus;
    return status === 'IN_PROGRESS' || status === 'AWAITING_DTP_PROCESS';
  });

  readonly isCancelledJob = computed(() => {
    return this.order()?.jobStatus === 'CANCELLED';
  });

  readonly canShowCancelAction = computed(() => {
    if (this.isCancelledJob()) {
      return false;
    }

    return this.isInProgressJob() || this.canCancelJob();
  });

  onCancelJobClick(): void {
    if (this.isCancelledJob()) {
      return;
    }

    if (this.isInProgressJob()) {
      this.openCancelDialog();
      return;
    }

    this.openLegacyCancelDialog();
  }

  private openLegacyCancelDialog(): void {
    const jobUid = this.order()?.jobUid;
    if (!jobUid || !this.canCancelJob()) {
      return;
    }

    this.popupService.open<boolean>({
      title: 'Cancel Job',
      width: '622px',
      maxWidth: '96vw',
      component: CancelJobConfirmContentComponent,
      componentInputs: {
        confirmAction: () => this.cancelJob(jobUid),
      },
      hideButtons: true,
      dialogTestId: 'cancel-job-confirmation-dialog',
    });
  }

  private openCancelDialog(): void {
    const dialogRef = this.matDialog.open<CancelJobScopeDialogComponent>(
      CancelJobScopeDialogComponent,
      {
        width: '622px',
        minWidth: '622px',
        autoFocus: true,
        disableClose: true,
      },
    );

    dialogRef.componentInstance.continueClicked.subscribe(
      (result: { scope: 'ENTIRE_JOB' | 'SPECIFIC_FILES' }) => {
        if (result.scope === 'ENTIRE_JOB') {
          this.cancelFlowState.update((state) => ({
            ...state,
            scope: result.scope,
          }));
          this.openCancelReasonDialog();
        } else if (result.scope === 'SPECIFIC_FILES') {
          this.cancelFlowState.update((state) => ({
            ...state,
            scope: result.scope,
          }));
          // TLS-2237: Open file selection modal (created in Phase 3 - US1)
          this.openFileSelectionDialog();
        }
        dialogRef.close();
      },
    );

    dialogRef.componentInstance.closeClicked.subscribe(() => {
      this.resetCancelFlowState();
      dialogRef.close();
    });
  }

  private openFileSelectionDialog(): void {
    const jobUid = this.order()?.jobUid;
    const files = this.order()?.files || [];

    if (!jobUid) {
      return;
    }

    const targetLocales: Record<string, string> = {};
    this.order()?.targetLocales?.forEach((locale) => {
      targetLocales[locale.code] = locale.name;
    });

    const dialogRef = this.matDialog.open(FileSelectionModalComponent, {
      width: '622px',
      minWidth: '622px',
      minHeight: '318px',
      maxHeight: 'min(622px, calc(100vh - 48px))',
      autoFocus: true,
      disableClose: true,
    });

    // Signal inputs must be set via componentRef.setInput() — data:{} only injects MAT_DIALOG_DATA
    dialogRef.componentRef!.setInput('jobUid', jobUid);
    dialogRef.componentRef!.setInput('files', files);
    dialogRef.componentRef!.setInput('targetLocales', targetLocales);

    const component = dialogRef.componentInstance;

    component.continuedWithFiles.subscribe((selectedFileUids: string[]) => {
      // User selected files and clicked Continue
      // Store selection in state for next step (confirmation modal in Phase 5)
      this.cancelFlowState.update((state) => ({
        ...state,
        selectedFileUids,
      }));
      this.openFileCancellationConfirmationDialog(selectedFileUids);
      dialogRef.close();
    });

    component.closed.subscribe(() => {
      this.resetCancelFlowState();
      dialogRef.close();
    });
  }

  private openFileCancellationConfirmationDialog(
    selectedFileUids: string[],
  ): void {
    const files = this.order()?.files || [];
    const targetLocales = this.order()?.targetLocales || [];

    // Group selected files by language for confirmation display
    const groupedFiles = this.groupFilesByLanguageForConfirmation(
      files,
      selectedFileUids,
      targetLocales,
    );

    const dialogRef = this.matDialog.open(ConfirmationModalComponent, {
      width: '622px',
      minWidth: '622px',
      minHeight: '318px',
      maxHeight: '550px',
      autoFocus: true,
      disableClose: true,
    });

    const component = dialogRef.componentInstance;
    component.languageGroups.set(groupedFiles);
    component.totalSelectedFiles.set(selectedFileUids.length);

    component.nextStep.subscribe(() => {
      // User proceeded to reason step
      this.openFileCancellationReasonDialog(selectedFileUids);
      dialogRef.close();
    });

    component.back.subscribe(() => {
      // User wants to go back to file selection
      this.openFileSelectionDialog();
      dialogRef.close();
    });

    component.closed.subscribe(() => {
      this.resetCancelFlowState();
      dialogRef.close();
    });
  }

  private openFileCancellationReasonDialog(selectedFileUids: string[]): void {
    const dialogRef = this.matDialog.open<CancelJobReasonDialogComponent>(
      CancelJobReasonDialogComponent,
      {
        width: '622px',
        minWidth: '622px',
        autoFocus: true,
        disableClose: true,
      },
    );

    dialogRef.componentInstance.confirmed.subscribe(
      (result: CancelReasonDialogResult) => {
        const reason = result.reason?.trim() ?? '';
        if (!result.confirmed || reason.length === 0) {
          return;
        }

        this.submitFileCancellation(selectedFileUids, reason);
        dialogRef.close();
      },
    );

    dialogRef.componentInstance.closed.subscribe(() => {
      this.resetCancelFlowState();
      dialogRef.close();
    });
  }

  private submitFileCancellation(
    selectedFileUids: string[],
    reason: string,
  ): void {
    const order = this.order();
    const jobUid = order?.jobUid;
    if (!jobUid) {
      return;
    }

    this.cancelFlowState.update((state) => ({
      ...state,
      isSubmitting: true,
    }));

    const files = order?.files || [];
    const filesArray = selectedFileUids.map((uid) => {
      const file = files.find((f) => f.id === uid);
      return {
        id: uid,
        translatedDate: file?.translatedDate,
        filename: file?.name || file?.originalName,
        language: file?.localeId,
      };
    });

    const request = {
      jobUid: jobUid,
      jobName: order?.customJobName,
      vendorName: order?.vendorName || order?.vendor?.name,
      email: order?.email,
      vendorEmail: order?.vendorEmail || order?.vendor?.email,
      filesArray,
      cancelAll: false,
      reason: reason.trim() || undefined,
    };

    const loadingRef = this.popupService.open({
      title: 'Cancel Files',
      showSpinner: true,
      message: 'Cancelling files...',
      centerContent: true,
      hideButtons: true,
      freeze: true,
      width: '622px',
      maxWidth: '96vw',
      dialogTestId: 'cancel-files-loading-dialog',
    });

    firstValueFrom(this.vendorService.cancelVendorOrderSpecificFiles(request))
      .then(() => {
        loadingRef.close();
        this.showFileCancellationSuccess(selectedFileUids.length);
        this.loadOrder(jobUid, true);
      })
      .catch(() => {
        loadingRef.close();
        this.cancelFlowState.update((state) => ({
          ...state,
          isSubmitting: false,
        }));
        this.toastService.showAtElement(
          this.hostEl.nativeElement,
          'Failed to cancel files. Please try again.',
          { type: 'error' },
        );
      });
  }

  private showFileCancellationSuccess(cancelledCount: number): void {
    const dialogRef = this.matDialog.open<CancelJobSuccessDialogComponent>(
      CancelJobSuccessDialogComponent,
      {
        width: '622px',
        minWidth: '622px',
        maxWidth: '622px',
        autoFocus: true,
        disableClose: true,
      },
    );

    dialogRef.componentRef!.setInput('cancelledCount', cancelledCount);

    dialogRef.componentInstance.closeClicked.subscribe(() => {
      this.resetCancelFlowState();
      dialogRef.close();
    });
  }

  private groupFilesByLanguageForConfirmation(
    files: VendorOrderFile[],
    selectedFileUids: string[],
    targetLocales: any[],
  ): any[] {
    // Group selected files by language for confirmation display
    const groupMap = new Map<string, any>();
    const localeNameLookup = new Map<string, string>();
    const languageLookup = LANGUAGES as Record<string, string>;

    targetLocales.forEach((locale: any) => {
      const code = typeof locale?.code === 'string' ? locale.code.trim() : '';
      const name = typeof locale?.name === 'string' ? locale.name.trim() : '';
      if (!code || !name) {
        return;
      }
      localeNameLookup.set(code, name);
      localeNameLookup.set(code.replaceAll('_', '-'), name);
    });

    selectedFileUids.forEach((fileUid) => {
      const file = files.find((f) => f.id === fileUid);
      if (!file) return;

      const rawLangCode =
        (typeof file.localeId === 'string' && file.localeId.trim()) ||
        (typeof file.target === 'string' && file.target.trim()) ||
        '';
      const normalizedLangCode = rawLangCode.replaceAll('_', '-');
      const langCode = rawLangCode || normalizedLangCode || 'unknown-language';
      const langLabel =
        localeNameLookup.get(rawLangCode) ||
        localeNameLookup.get(normalizedLangCode) ||
        languageLookup[rawLangCode] ||
        languageLookup[normalizedLangCode] ||
        normalizedLangCode ||
        'Unknown language';

      if (!groupMap.has(langCode)) {
        groupMap.set(langCode, {
          targetLanguageCode: langCode,
          targetLanguageLabel: langLabel,
          expanded: true,
          files: [],
          cancellableTotal: 0,
          selectedCancellableCount: 0,
        });
      }

      const group = groupMap.get(langCode);
      group.files.push({
        fileUid: file.id,
        fileName: file.originalName || file.uploadName || '',
        // Confirmation modal only contains files already selected by user.
        isCancellable: true,
        isCancelled: file.isCancelled || false,
        targetLanguageCode: langCode,
      });
    });

    return Array.from(groupMap.values());
  }

  private openCancelReasonDialog(): void {
    const dialogRef = this.matDialog.open<CancelJobReasonDialogComponent>(
      CancelJobReasonDialogComponent,
      {
        width: '622px',
        minWidth: '622px',
        autoFocus: true,
        disableClose: true,
      },
    );

    dialogRef.componentInstance.confirmed.subscribe(
      (result: CancelReasonDialogResult) => {
        const reason = result.reason?.trim() ?? '';
        if (!result.confirmed || reason.length === 0) {
          return;
        }

        this.submitCancelEntireJob(reason);
        dialogRef.close();
      },
    );

    dialogRef.componentInstance.closed.subscribe(() => {
      this.resetCancelFlowState();
      dialogRef.close();
    });
  }

  private submitCancelEntireJob(reason: string): void {
    const order = this.order();
    const jobUid = order?.jobUid;
    if (!jobUid) {
      return;
    }

    this.cancelFlowState.update((state) => ({
      ...state,
      reason,
      isSubmitting: true,
    }));

    // Filter files that are not Completed or Cancelled (IN_PROGRESS or AWAITING_DTP_PROCESS)
    const files = order?.files || [];
    const cancellableFiles = files.filter((file) => {
      const status = file.status;
      return (
        status !== 'COMPLETED' && status !== 'CANCELLED' && !file.isCancelled
      );
    });

    // Build filesArray with the same structure as specific files
    const filesArray = cancellableFiles.map((file) => ({
      id: file.id || '',
      translatedDate: file.translatedDate,
      filename: file.name || file.originalName,
      language: file.localeId,
    }));

    // Build request with all required fields
    const request = {
      jobUid: jobUid,
      jobName: order?.customJobName,
      vendorName: order?.vendorName || order?.vendor?.name,
      email: order?.email,
      vendorEmail: order?.vendorEmail || order?.vendor?.email,
      filesArray,
      cancelAll: true,
      reason: reason.trim() || undefined,
    };

    const loadingRef = this.popupService.open({
      title: 'Cancel Job',
      showSpinner: true,
      message: 'Cancelling job...',
      centerContent: true,
      hideButtons: true,
      freeze: true,
      width: '622px',
      maxWidth: '96vw',
      dialogTestId: 'cancel-job-loading-dialog',
    });

    this.vendorService
      .cancelVendorOrder(request)
      .pipe(
        finalize(() => {
          loadingRef.close();
          this.cancelFlowState.update((state) => ({
            ...state,
            isSubmitting: false,
          }));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.cancelFlowState.update((state) => ({
            ...state,
            reason,
          }));
          this.openCancelSuccessDialog();
          this.loadOrder(jobUid, true);
        },
        error: () => {
          this.toastService.showAtElement(
            this.hostEl.nativeElement,
            'Unable to cancel this job right now. Please try again.',
            { type: 'error' },
          );
        },
      });
  }

  private openCancelSuccessDialog(): void {
    const dialogRef = this.matDialog.open<CancelJobSuccessDialogComponent>(
      CancelJobSuccessDialogComponent,
      {
        width: '622px',
        minWidth: '622px',
        maxWidth: '622px',
        autoFocus: true,
        disableClose: true,
      },
    );

    dialogRef.componentInstance.closeClicked.subscribe(() => {
      dialogRef.close();
    });
  }

  private resetCancelFlowState(): void {
    this.cancelFlowState.set({
      scope: null,
      reason: '',
      isSubmitting: false,
    });
  }

  readonly messagesPolicy = computed<MessagePolicyEvaluation>(() => {
    const order = this.order();
    if (!order) {
      return {
        state: 'HIDDEN',
        anchorEpochMs: null,
        cutoffEpochMs: null,
        reason: 'Missing order details',
      };
    }

    return computeMessagePolicy({
      jobStatus: order.jobStatus,
      purchaseOrderId: order.purchaseOrderId,
      cancellationDate: order.cancellationDate,
      translationCompleteDate: order.translationCompleteDate,
      nowEpochMs: Date.now(),
    });
  });

  readonly isInProgressOrLater = computed(() => {
    const policyState = this.messagesPolicy().state;
    if (policyState === 'INTERACTIVE' || policyState === 'READ_ONLY') {
      return true;
    }

    const status = this.order()?.jobStatus;
    if (!status) return false;
    const normalized = status.toUpperCase().replaceAll(/[\s-]/g, '_');
    return (MESSAGES_LEGACY_ENABLED_STATUSES as readonly string[]).includes(
      normalized,
    );
  });

  readonly isMessagesReadOnly = computed(() => {
    const policyState = this.messagesPolicy().state;
    if (policyState === 'READ_ONLY') {
      return true;
    }
    if (policyState === 'INTERACTIVE') {
      return false;
    }

    const status = this.order()?.jobStatus;
    if (!status) return true;
    const normalized = status.toUpperCase().replaceAll(/[\s-]/g, '_');
    return (MESSAGES_LEGACY_READONLY_STATUSES as readonly string[]).includes(
      normalized,
    );
  });

  readonly tabsNav = viewChild<ElementRef<HTMLElement>>('tabsNav');
  readonly tabButtons =
    viewChildren<ElementRef<HTMLButtonElement>>('tabButton');
  readonly breadcrumbLevels = computed<BreadcrumbLevel[]>(() => {
    const detail = this.jobDetail();
    const levels: BreadcrumbLevel[] = [
      {
        label: 'Translation Manager',
        route: '/translation-manager',
        state: {
          fromBreadcrumb: true,
          selectedFilters: this.selectedFilters(),
          notificationSnapshot: this.notificationSnapshotOnEntry(),
        },
      },
    ];

    if (detail?.jobUid) {
      levels.push({ label: detail.jobUid });
    }

    return levels;
  });

  readonly jobDetail = computed<JobDetailView | null>(() => {
    const order = this.order();
    if (!order) {
      return null;
    }
    return this.mapOrderToView(order);
  });

  readonly finalCostIncreaseBanner = computed<FinalCostIncreaseBanner | null>(
    () => {
      const order = this.order();
      const context = this.getFinalCostReviewContext();
      if (!context || !order) return null;

      if (order.jobStatus !== 'IN_PROGRESS') {
        return null;
      }

      if ('userAcceptedQuoteDate' in context.reanalysis) {
        return null;
      }

      const percentIncrease =
        ((context.reanalysisCost - context.approvedCost) /
          context.approvedCost) *
        100;

      return {
        percent: this.formatCompactNumber(percentIncrease),
        currency: toIsoCurrency(
          context.reanalysis.currency ??
            context.acceptedQuote.currency ??
            context.estimate.currency,
        ),
        amount: this.formatCompactNumber(context.reanalysisCost),
        estimateName: context.estimate.name,
      };
    },
  );

  readonly fullOrder = computed(() => this.order());
  readonly canCancelJob = computed(() =>
    isJobStatusCancellable(this.order()?.jobStatus),
  );
  readonly isRejectedRequest = computed(
    () => this.order()?.jobStatus === 'REJECTED',
  );
  readonly rejectedSupportHref = computed(
    () =>
      `mailto:${REJECTED_REQUEST_SUPPORT_EMAIL}?subject=${encodeURIComponent(REJECTED_REQUEST_SUPPORT_SUBJECT)}`,
  );

  ngOnInit(): void {
    this.selectedFilters.set(this.getSelectedFiltersFromState());
    this.notificationSnapshotOnEntry.set(
      this.notificationService.notificationSnapshotKey(),
    );

    const jobUid = this.route.snapshot.paramMap.get('jobUid');
    if (!jobUid) {
      this.errorMessage.set('Unable to open this job. Missing job ID.');
      this.isLoading.set(false);
      this.isFilesLoading.set(false);
      return;
    }
    this.loadOrder(jobUid);
  }

  ngAfterViewInit(): void {
    this.syncTabIndicator();
  }

  ngOnDestroy(): void {
    this.clearTabTransitionTimers();
    if (this.reloadTimerId) {
      clearTimeout(this.reloadTimerId);
      this.reloadTimerId = null;
    }
    if (this.hasWindow) {
      window.removeEventListener('resize', this.handleWindowResize);
    }
    this.toolbarTitleService.clearContextualTitle();
  }

  setActiveTab(tab: JobDetailTabId): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.syncTabIndicator();

    const jobUid = this.route.snapshot.paramMap.get('jobUid') ?? '';

    if (
      tab === 'quotes' &&
      this.notificationService.isAwaitingVendorJob(jobUid)
    ) {
      this.notificationService.acknowledgeVendorJob(jobUid);
    }

    if (
      tab === 'translated-files' &&
      this.notificationService.isCompletedDocsJob(jobUid)
    ) {
      this.notificationService.acknowledgeCompletedJob(jobUid);
    }

    if (tab === 'messages' && this.hasNewMessage()) {
      this.hasNewMessage.set(false);
      this.notificationService.decrementNewMessageNotification();
      if (this.hasWindow) {
        const { newMessage: _, ...rest } = (history.state ?? {}) as Record<
          string,
          unknown
        >;
        history.replaceState(rest, '');
      }
    }

    this.clearTabTransitionTimers();
    this.tabContentState.set('exiting');

    this.fadeExitTimerId = setTimeout(() => {
      this.renderedTab.set(tab);
      this.tabContentState.set('entering');

      this.fadeEnterTimerId = setTimeout(() => {
        this.tabContentState.set('idle');
        this.fadeEnterTimerId = null;
      }, JobDetailComponent.TAB_FADE_DURATION_MS);

      this.fadeExitTimerId = null;
    }, JobDetailComponent.TAB_FADE_DURATION_MS);
  }

  onTabsKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }

    event.preventDefault();

    const currentIndex = this.tabs().findIndex(
      (tab) => tab.id === this.activeTab(),
    );
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex =
      (currentIndex + delta + this.tabs().length) % this.tabs().length;
    const nextTab = this.tabs()[nextIndex];

    this.setActiveTab(nextTab.id);
    this.tabButtons()[nextIndex]?.nativeElement.focus();
  }

  getTabAriaControls(tabId: JobDetailTabId): string {
    return `job-detail-tab-panel-${tabId}`;
  }

  getTabId(tabId: JobDetailTabId): string {
    return `job-detail-tab-${tabId}`;
  }

  getRenderedTabPanelLabel(): string {
    return (
      this.tabs().find((tab) => tab.id === this.renderedTab())?.label ?? ''
    );
  }

  handleWindowResize = (): void => {
    this.syncTabIndicator();
  };

  private clearTabTransitionTimers(): void {
    if (this.fadeExitTimerId) {
      clearTimeout(this.fadeExitTimerId);
      this.fadeExitTimerId = null;
    }

    if (this.fadeEnterTimerId) {
      clearTimeout(this.fadeEnterTimerId);
      this.fadeEnterTimerId = null;
    }
  }

  private syncTabIndicator(): void {
    requestAnimationFrame(() => {
      const navElement = this.tabsNav()?.nativeElement;
      const activeIndex = this.tabs().findIndex(
        (tab) => tab.id === this.activeTab(),
      );
      const activeButtonElement = this.tabButtons()[activeIndex]?.nativeElement;

      if (!navElement || !activeButtonElement) {
        this.tabIndicatorWidth.set(0);
        this.tabIndicatorOffset.set(0);
        return;
      }

      const navRect = navElement.getBoundingClientRect();
      const activeRect = activeButtonElement.getBoundingClientRect();

      this.tabIndicatorOffset.set(activeRect.left - navRect.left);
      this.tabIndicatorWidth.set(activeRect.width);
    });
  }

  constructor() {
    if (this.hasWindow) {
      window.addEventListener('resize', this.handleWindowResize);
    }

    effect(() => {
      this.tabButtons();
      this.activeTab();
      this.syncTabIndicator();
    });
  }

  toggleFileSort(table: FileTableType, column: FileSortColumn): void {
    this.getSortSignal(table).update((state) => {
      if (state.column === column) {
        return { ...state, descending: !state.descending };
      }

      return {
        column,
        descending: false,
      };
    });
  }

  getFileAriaSort(
    table: FileTableType,
    column: FileSortColumn,
  ): 'ascending' | 'descending' | null {
    const state = this.getSortSignal(table)();

    if (state.column !== column) {
      return null;
    }

    return state.descending ? 'descending' : 'ascending';
  }

  navigateBack(): void {
    this.router.navigate(['/translation-manager'], {
      state: {
        fromDetail: true,
        selectedFilters: this.selectedFilters(),
        notificationSnapshot: this.notificationSnapshotOnEntry(),
      },
    });
  }

  downloadSourceFile(fileName: string): void {
    void this.downloadFile(fileName, TranslationFileType.SOURCE);
  }

  downloadReferenceFile(fileName: string): void {
    void this.downloadFile(fileName, TranslationFileType.REFERENCE);
  }

  onQuoteConfirmed(jobUid: string): void {
    this.loadOrder(jobUid, true);
  }

  onReviewFinalCostClick(): void {
    const order = this.order();
    const context = this.getFinalCostReviewContext();
    if (!order || !context) return;

    this.openReviewFinalCostDialog(order, context);
  }

  private openReviewFinalCostDialog(
    order: VendorOrder,
    context: FinalCostReviewContext,
  ): void {
    const dialogRef = this.popupService.open<FinalCostDialogResult>({
      title: 'Review Final Cost.',
      width: '680px',
      maxWidth: '96vw',
      cancelLabel: 'Reject',
      cancelResult: 'reject',
      confirmLabel: 'Approve',
      component: ReviewFinalCostModalComponent,
      componentInputs: {
        order,
        estimate: context.estimate,
        acceptedQuote: context.acceptedQuote,
        reanalysis: context.reanalysis,
      },
      dialogTestId: 'review-final-cost-dialog',
      cancelButtonTestId: 'review-final-cost-reject',
      confirmButtonTestId: 'review-final-cost-approve',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === 'reject') {
          this.openRejectFinalCostDialog(order, context);
          return;
        }

        if (result === true) {
          this.openApproveFinalCostDialog(order, context);
        }
      });
  }

  private openRejectFinalCostDialog(
    order: VendorOrder,
    context: FinalCostReviewContext,
  ): void {
    const dialogRef = this.popupService.open<RejectFinalCostDialogResult>({
      title: 'Reject Final Cost',
      width: '680px',
      maxWidth: '96vw',
      cancelLabel: 'No',
      confirmLabel: 'Yes',
      component: RejectFinalCostModalComponent,
      dialogTestId: 'reject-final-cost-dialog',
      cancelButtonTestId: 'reject-final-cost-no',
      confirmButtonTestId: 'reject-final-cost-yes',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === 'back') {
          this.openReviewFinalCostDialog(order, context);
          return;
        }

        if (result === true) {
          this.submitFinalCostDecision('reject', order, context);
        }
      });
  }

  private openApproveFinalCostDialog(
    order: VendorOrder,
    context: FinalCostReviewContext,
  ): void {
    const dialogRef = this.popupService.open<ApproveFinalCostDialogResult>({
      title: 'Approve Final Cost',
      width: '680px',
      maxWidth: '96vw',
      cancelLabel: 'Cancel',
      confirmLabel: 'Confirm',
      component: ApproveFinalCostModalComponent,
      componentInputs: {
        order,
        estimate: context.estimate,
        reanalysis: context.reanalysis,
      },
      dialogTestId: 'approve-final-cost-dialog',
      cancelButtonTestId: 'approve-final-cost-cancel',
      confirmButtonTestId: 'approve-final-cost-confirm',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === 'back') {
          this.openReviewFinalCostDialog(order, context);
          return;
        }

        if (this.isApproveFinalCostResult(result)) {
          this.submitFinalCostDecision('accept', order, context, result);
        }
      });
  }

  private submitFinalCostDecision(
    decision: 'accept' | 'reject',
    order: VendorOrder,
    context: FinalCostReviewContext,
    result?: ApproveFinalCostResult,
  ): void {
    const jobUid = order.jobUid?.trim();
    const vendorName = (
      context.estimate.name ??
      order.vendorName ??
      order.vendor?.name ??
      ''
    ).trim();
    if (!jobUid || !vendorName) return;

    const loadingRef = this.popupService.open({
      title: decision === 'accept' ? 'Approve Final Cost' : 'Reject Final Cost',
      showSpinner: true,
      message:
        decision === 'accept'
          ? 'Approving final cost...'
          : 'Rejecting final cost...',
      centerContent: true,
      hideButtons: true,
      freeze: true,
      width: '622px',
      maxWidth: '96vw',
      dialogTestId: `${decision}-final-cost-loading-dialog`,
    });

    const request$ =
      decision === 'accept'
        ? this.vendorService.acceptFinalCost(
            jobUid,
            vendorName,
            result?.purchaseOrderId ?? '',
          )
        : this.vendorService.rejectFinalCost(jobUid, vendorName);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        loadingRef.close();
        const message =
          decision === 'accept'
            ? `You have approved ${vendorName}'s final cost. The vendor will now start working on your translation. You will receive a notification once your job is complete.`
            : `You have rejected ${vendorName}'s final cost and the translation job has been cancelled.`;
        const successDialogRef = this.popupService.open({
          title: 'Success!',
          component: ApproveFinalCostSuccessComponent,
          componentInputs: { message },
          hideButtons: true,
          dialogTestId:
            decision === 'accept'
              ? 'approve-final-cost-success'
              : 'reject-final-cost-success',
        });

        successDialogRef
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.loadOrder(jobUid, true);
          });
      },
      error: () => {
        loadingRef.close();
        this.toastService.showAtElement(
          this.hostEl.nativeElement,
          `Unable to ${decision === 'accept' ? 'approve' : 'reject'} the final cost right now. Please try again.`,
          { type: 'error' },
        );
      },
    });
  }

  private isApproveFinalCostResult(
    result: ApproveFinalCostDialogResult,
  ): result is ApproveFinalCostResult {
    return (
      typeof result === 'object' &&
      result !== null &&
      typeof result.purchaseOrderId === 'string'
    );
  }

  private async cancelJob(jobUid: string): Promise<void> {
    try {
      await firstValueFrom(this.vendorService.cancelVendorOrder(jobUid));
      this.loadOrder(jobUid, true);
      this.popupService.open({
        title: 'Cancel Job',
        component: CancelJobSuccessContentComponent,
        hideButtons: true,
        dialogTestId: 'cancel-job-success-message',
      });
    } catch (err: unknown) {
      this.loadOrder(jobUid, true);
      this.popupService.open({
        title: 'Unable to cancel job',
        message: this.vendorService.handleErrors(err as never),
        hideButtons: true,
      });
    }
  }

  onJobCompleted(): void {
    const jobUid = this.route.snapshot.paramMap.get('jobUid');
    if (jobUid) {
      // Delay reload to allow backend to process the status update and prevent rapid reload loops
      if (this.reloadTimerId) {
        clearTimeout(this.reloadTimerId);
      }

      this.reloadTimerId = setTimeout(() => {
        this.reloadTimerId = null;
        this.loadOrder(jobUid, true);
      }, 1000);
    }
  }

  private loadOrder(jobUid: string, _forceRefresh = false): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.sourceFiles.set([]);
    this.referenceFiles.set([]);

    this.vendorOrdersService
      .getJobDetail(jobUid, this.vendorName, this.rawJobStatus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (order) => {
          if (order) {
            if (!this.isOwnedByCurrentUser(order)) {
              this.redirectUnauthorizedAccess();
              return;
            }

            this.order.set(order);
            this.toolbarTitleService.setContextualTitle(
              this.buildRequestToolbarTitle(order.jobNumber, order.jobUid),
            );
            void this.loadFolderFiles(jobUid, order.isDtpJob, order);
          } else {
            this.errorMessage.set('Translation job not found.');
            this.isFilesLoading.set(false);
            this.toolbarTitleService.clearContextualTitle();
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.isFilesLoading.set(false);
          this.toolbarTitleService.clearContextualTitle();
          this.errorMessage.set(
            'Unable to load job details right now. Please try again.',
          );
        },
      });
  }

  private isOwnedByCurrentUser(order: VendorOrder): boolean {
    const currentUserPrid = this.authService.getPrid()?.trim().toLowerCase();
    const orderPrid = order.prid?.trim().toLowerCase();

    return Boolean(
      currentUserPrid && orderPrid && currentUserPrid === orderPrid,
    );
  }

  private redirectUnauthorizedAccess(): void {
    this.isLoading.set(false);
    this.isFilesLoading.set(false);
    this.order.set(null);
    this.sourceFiles.set([]);
    this.referenceFiles.set([]);
    this.errorMessage.set(null);
    this.toolbarTitleService.clearContextualTitle();
    void this.router.navigateByUrl('/');
  }

  private mapOrderToView(order: VendorOrder): JobDetailView {
    const sourceFiles = this.sourceFiles();
    const referenceFiles = this.referenceFiles();
    const isDtpJob = order.isDtpJob === true;
    const useRevised = this.shouldUseRevisedClassification(
      order.jobStatus,
      order.vendor,
    );
    const resolvedFiles = this.resolveClassificationFiles(
      order.files,
      useRevised,
    );
    const { basicFormatFiles, dtpFiles } =
      this.splitSourceFilesByClassification(
        sourceFiles,
        resolvedFiles,
        isDtpJob,
      );

    const contentType =
      order.contentType ?? order.files?.[0]?.contentType ?? '--';

    const sourceLocaleId =
      order.sourceLocaleId ??
      order.files?.find((f) => f.sourceLocaleId)?.sourceLocaleId;
    const targetLocaleIds: string[] = order.targetLocaleId?.length
      ? order.targetLocaleId
      : [
          ...new Set(
            (order.files ?? [])
              .map((f) => f.localeId)
              .filter((l): l is string => Boolean(l)),
          ),
        ];
    const statusLabel = this.getStatusLabel(order.jobStatus);

    return {
      jobUid: order.jobUid ?? '--',
      jobNumber: order.jobNumber ?? '--',
      jobName: order.customJobName ?? '--',
      requestDate: this.formatDateValue(order.createdAt),
      targetDate: this.formatDateValue(this.getTargetDate(order)),
      statusLabel,
      statusKey: this.getStatusKey(statusLabel),
      statusIcon: this.getStatusIcon(statusLabel),
      contentType: contentType,
      therapeuticArea: order.therapeuticArea ?? '--',
      costCentre: order.costCentre ?? '--',
      targetAudience: order.targetAudience ?? '--',
      translationCertificate: order.translationCertificateRequired
        ? 'Yes'
        : 'No',
      documentTone: order.documentTone ?? '--',
      additionalInfo: order.additionalInfo || '',
      cancellationReason: order.cancellationReason?.trim() ?? '',
      cancellationDate: order.cancellationDate
        ? this.formatDateValue(order.cancellationDate)
        : undefined,
      sourceLanguage: this.getLocaleName(sourceLocaleId),
      targetLanguages: this.getTargetLanguages(targetLocaleIds),
      basicFormatFiles: this.sortFiles(basicFormatFiles, this.basicFilesSort()),
      dtpFiles: this.sortFiles(dtpFiles, this.dtpFilesSort()),
      referenceFiles: this.sortFiles(referenceFiles, this.referenceFilesSort()),
    };
  }

  private shouldUseRevisedClassification(
    jobStatus: string | undefined,
    vendor: { name: string; id: string } | undefined,
  ): boolean {
    if (!jobStatus || !QUALIFYING_STATUSES_FOR_REVISED_DTP.has(jobStatus)) {
      return false;
    }
    if (jobStatus === 'CANCELLED' && !vendor?.id) {
      return false;
    }
    return true;
  }

  private resolveClassificationFiles(
    files: VendorOrderFile[] | undefined,
    useRevised: boolean,
  ): VendorOrderFile[] {
    if (!files) {
      return [];
    }
    if (!useRevised) {
      return files;
    }
    return files.map((file) =>
      typeof file.isDtpFileRevised === 'boolean'
        ? { ...file, isDtpFile: file.isDtpFileRevised }
        : file,
    );
  }

  private splitSourceFilesByClassification(
    sourceFiles: SourceFile[],
    orderFiles: VendorOrderFile[] | undefined,
    isDtpJob: boolean,
  ): { basicFormatFiles: SourceFile[]; dtpFiles: SourceFile[] } {
    const classifiedOrderFiles = (orderFiles ?? []).filter(
      (file): file is VendorOrderFile & { name: string; isDtpFile: boolean } =>
        typeof file?.name === 'string' &&
        file.name.trim().length > 0 &&
        typeof file.isDtpFile === 'boolean',
    );

    if (classifiedOrderFiles.length === 0) {
      return isDtpJob
        ? { basicFormatFiles: [], dtpFiles: [...sourceFiles] }
        : { basicFormatFiles: [...sourceFiles], dtpFiles: [] };
    }

    const dtpCounts = new Map<string, number>();
    const nonDtpCounts = new Map<string, number>();

    for (const file of classifiedOrderFiles) {
      const key = this.normalizeFileKey(
        file.originalPdfName?.trim() || file.name,
      );
      const targetMap = file.isDtpFile ? dtpCounts : nonDtpCounts;
      targetMap.set(key, (targetMap.get(key) ?? 0) + 1);
    }

    const basicFormatFiles: SourceFile[] = [];
    const dtpFiles: SourceFile[] = [];

    for (const sourceFile of sourceFiles) {
      const key = this.normalizeFileKey(sourceFile.name);
      const remainingDtp = dtpCounts.get(key) ?? 0;
      const remainingNonDtp = nonDtpCounts.get(key) ?? 0;

      if (remainingDtp > 0) {
        dtpFiles.push(sourceFile);
        dtpCounts.set(key, remainingDtp - 1);
        continue;
      }

      if (remainingNonDtp > 0) {
        basicFormatFiles.push(sourceFile);
        nonDtpCounts.set(key, remainingNonDtp - 1);
        continue;
      }

      if (isDtpJob) {
        dtpFiles.push(sourceFile);
      } else {
        basicFormatFiles.push(sourceFile);
      }
    }

    return {
      basicFormatFiles,
      dtpFiles,
    };
  }

  private normalizeFileKey(name: string): string {
    return name.trim().toLowerCase();
  }

  private getAcceptedQuote(estimate: Estimate): Quote | undefined {
    if (this.resolveRushedSelected(estimate)) {
      return estimate.quote?.rushed ?? estimate.quote?.normal;
    }

    return estimate.quote?.normal ?? estimate.quote?.rushed;
  }

  private getFinalCostReviewContext(): FinalCostReviewContext | null {
    for (const estimate of this.order()?.estimates ?? []) {
      if (!this.isEstimateApproved(estimate)) continue;

      const acceptedQuote = this.getAcceptedQuote(estimate);
      const reanalysis = estimate.quote?.reanalysis;
      const approvedCost = this.getQuoteTotalCost(acceptedQuote, estimate);
      const reanalysisCost = this.getQuoteTotalCost(reanalysis);

      if (
        !acceptedQuote ||
        !reanalysis ||
        approvedCost == null ||
        approvedCost <= 0 ||
        reanalysisCost == null ||
        reanalysisCost <= approvedCost
      ) {
        continue;
      }

      return {
        estimate,
        acceptedQuote,
        reanalysis,
        approvedCost,
        reanalysisCost,
      };
    }

    return null;
  }

  private resolveRushedSelected(estimate: Estimate): boolean {
    return estimate.rushedDateSelected === true;
  }

  private isEstimateApproved(estimate: Estimate): boolean {
    const quote = estimate.quote as
      | {
          acceptedBy?: string;
          acceptedDate?: string;
          normal?: { acceptedBy?: string; acceptedDate?: string };
          rushed?: { acceptedBy?: string; acceptedDate?: string };
        }
      | undefined;

    const acceptedBy =
      quote?.acceptedBy?.trim() ??
      quote?.normal?.acceptedBy?.trim() ??
      quote?.rushed?.acceptedBy?.trim();
    const acceptedDate =
      quote?.acceptedDate?.trim() ??
      quote?.normal?.acceptedDate?.trim() ??
      quote?.rushed?.acceptedDate?.trim();

    return !!acceptedBy && !!acceptedDate;
  }

  private getQuoteTotalCost(
    quote: Quote | undefined,
    estimate?: Estimate,
  ): number | null {
    const totalCost =
      quote?.totalCost ?? estimate?.priceMax ?? estimate?.priceMin;
    return typeof totalCost === 'number' && Number.isFinite(totalCost)
      ? totalCost
      : null;
  }

  private formatCompactNumber(value: number): string {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
    }).format(value);
  }

  private async downloadFile(
    fileName: string,
    fileType: TranslationFileType,
  ): Promise<void> {
    const jobUid = this.order()?.jobUid;
    if (!jobUid) {
      return;
    }

    try {
      const { url } = await this.s3FilesService.getPresignedUrlForFile(
        fileName,
        S3DestinationFolder.TRANSLATION,
        true,
        jobUid,
        fileType,
      );

      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = fileName;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
    } catch {
      // Ignore download errors; users can retry.
    }
  }

  private async loadFolderFiles(
    jobUid: string,
    isDtpJob?: boolean,
    order?: VendorOrder,
  ): Promise<void> {
    if (!jobUid) {
      this.sourceFiles.set([]);
      this.referenceFiles.set([]);
      this.isFilesLoading.set(false);
      return;
    }

    this.isFilesLoading.set(true);

    try {
      const [sourceResponse, referenceResponse] = await Promise.all([
        this.s3FilesService.getFilesInFolderWithSize(
          TranslationFileType.SOURCE,
          jobUid,
        ),
        this.s3FilesService.getFilesInFolderWithSize(
          TranslationFileType.REFERENCE,
          jobUid,
        ),
      ]);

      const generatedSourceFileNames = new Set(
        (order?.files ?? [])
          .filter(
            (file) =>
              typeof file.originalPdfName === 'string' &&
              file.originalPdfName.trim().length > 0,
          )
          .map((file) => this.normalizeFileKey(file.name)),
      );
      const mappedSourceFiles = (sourceResponse?.files ?? [])
        .filter(
          (file) =>
            !generatedSourceFileNames.has(this.normalizeFileKey(file.name)),
        )
        .map((file) => ({
          name: file.name,
          size: this.formatBytes(file.size),
          bytes: file.size,
        }));

      const mappedReferenceFiles = (referenceResponse?.files ?? []).map(
        (file) => ({
          name: file.name,
          size: this.formatBytes(file.size),
          bytes: file.size,
        }),
      );

      // Extract reference files from API response (fileType === "attachment")
      const apiReferenceFiles =
        this.extractReferenceFilesFromApiResponse(order);

      // Merge S3 files with API response files, removing duplicates
      const mergedReferenceFiles = this.mergeReferenceFiles(
        mappedReferenceFiles,
        apiReferenceFiles,
      );

      // Show all reference files without filtering.
      // S3 REFERENCE folder already separates reference files from source files.
      // All files in this folder are intentional references the user uploaded.
      this.sourceFiles.set(mappedSourceFiles);
      this.referenceFiles.set(mergedReferenceFiles);
    } catch {
      this.sourceFiles.set([]);
      this.referenceFiles.set([]);
    } finally {
      this.isFilesLoading.set(false);
    }
  }

  /**
   * Extracts reference files from the API response using the fileType field.
   * Reference files have fileType === "attachment".
   */
  private extractReferenceFilesFromApiResponse(
    order?: VendorOrder,
  ): SourceFile[] {
    if (!order?.files || order.files.length === 0) {
      return [];
    }

    return (order.files ?? [])
      .filter(
        (file): file is VendorOrderFile & { name: string } =>
          file?.fileType === 'attachment' &&
          typeof file.name === 'string' &&
          file.name.trim().length > 0,
      )
      .map((file) => ({
        name: file.name,
        size: '--', // Size not available from API response
        bytes: 0,
      }));
  }

  /**
   * Merges reference files from S3 and API response, preferring S3 files
   * (which have actual sizes) over API files.
   */
  private mergeReferenceFiles(
    s3Files: SourceFile[],
    apiFiles: SourceFile[],
  ): SourceFile[] {
    // Track all seen keys to deduplicate across both S3 and API files
    const seenKeys = new Set<string>();

    const merged: SourceFile[] = [];

    for (const file of s3Files) {
      seenKeys.add(this.normalizeFileKey(file.name));
      merged.push(file);
    }

    // Add API files that haven't already been added (from S3 or earlier in the API list)
    for (const apiFile of apiFiles) {
      const key = this.normalizeFileKey(apiFile.name);
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        merged.push(apiFile);
      }
    }

    return merged;
  }

  private buildRequestToolbarTitle(
    jobNumber?: string,
    fallbackUid?: string,
  ): string | null {
    const cleanedJobNumber = jobNumber?.replace(/^AZT-/i, '').trim() ?? '';

    if (cleanedJobNumber.length > 0) {
      return `Request ${cleanedJobNumber}`;
    }

    const cleanedFallbackUid = fallbackUid?.trim() ?? '';
    if (cleanedFallbackUid.length > 0) {
      return `Request ${cleanedFallbackUid}`;
    }

    return null;
  }

  private getSelectedFiltersFromState(): string[] {
    const state = history.state as { selectedFilters?: string[] } | null;
    if (!Array.isArray(state?.selectedFilters)) {
      return [];
    }

    return state.selectedFilters.filter(
      (value) => typeof value === 'string' && value.trim().length > 0,
    );
  }

  private formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes)) {
      return '--';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }

    return `${this.formatDecimal(size)} ${units[unitIndex]}`;
  }

  private formatDecimal(value: number): string {
    if (Number.isInteger(value)) {
      return value.toString();
    }

    const trimmed = value
      .toFixed(2)
      .replace(/\.0+$/, '')
      .replace(/(\.\d*?)0+$/, '$1');
    return trimmed;
  }

  private sortFiles(
    files: SourceFile[],
    sortState: FileSortState,
  ): SourceFile[] {
    const direction = sortState.descending ? -1 : 1;

    return [...files].sort((left, right) => {
      if (sortState.column === 'name') {
        return (
          left.name.localeCompare(right.name, undefined, {
            sensitivity: 'base',
          }) * direction
        );
      }

      return (left.bytes - right.bytes) * direction;
    });
  }

  private getSortSignal(table: FileTableType) {
    switch (table) {
      case 'basic':
        return this.basicFilesSort;
      case 'dtp':
        return this.dtpFilesSort;
      default:
        return this.referenceFilesSort;
    }
  }

  private getTargetDate(order: VendorOrder): string | undefined {
    return order.fixedDeadline ?? order.deadline;
  }

  private formatDateValue(value?: number | string): string {
    if (value === undefined || value === null) {
      return '--';
    }

    const dateValue =
      typeof value === 'number' ? new Date(value) : new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
      return '--';
    }

    return formatDate(dateValue, 'dd/MMM/yyyy', 'en-GB');
  }

  private getLocaleName(localeId?: string): string {
    if (!localeId) {
      return '--';
    }
    return LOCALE_LOOKUP.get(localeId) ?? localeId;
  }

  private getTargetLanguages(localeIds?: string[]): string[] {
    if (!localeIds || localeIds.length === 0) {
      return [];
    }
    return localeIds.map((id) => this.getLocaleName(id));
  }

  private getStatusLabel(status?: string): string {
    if (!status) {
      return '--';
    }
    const mapped =
      JOB_STATUSES_NEW_TAGS[status as keyof typeof JOB_STATUSES_NEW_TAGS];
    return mapped ?? this.formatFallbackStatus(status);
  }

  private formatFallbackStatus(status: string): string {
    return status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (m) => m.toUpperCase());
  }

  private getStatusKey(statusLabel?: string): string {
    if (!statusLabel || statusLabel === '--') {
      return 'unknown';
    }
    return statusLabel
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  private getStatusIcon(statusLabel?: string): string | null {
    if (!statusLabel || statusLabel === '--') {
      return null;
    }
    return STATUS_ICON_MAP[statusLabel] ?? null;
  }

  cancelledReasonLabel(): string {
    const reason = this.jobDetail()?.cancellationReason?.trim() ?? '';

    if (reason.length === 0) {
      return 'No cancellation reason provided.';
    }

    const hasReanalysis = this.order()?.estimates?.some(
      (estimate) => !!estimate.quote?.reanalysis,
    );

    return formatCancellationReason(reason, hasReanalysis) || reason;
  }

  isReanalysisApprovalExpiredCancellation(): boolean {
    return (
      this.jobDetail()?.cancellationReason?.trim() ===
      CANCELLATION_REASON_REANALYSIS_APPROVAL_EXPIRED
    );
  }
}
